import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./Production.css";

const Production = () => {
  // ─── Process Data ───
  const productionProcess = [
    { title: "Design & Planning", desc: "Your tech pack becomes a production-ready blueprint — patterns, fabric consumption, and a committed critical path. Accuracy at this stage determines how smoothly the remainder of the order runs.", image: "/img/Production/prod.jpg" },
    { title: "Material Selection", desc: "Fabric and trims are bought to specification from suppliers we have already checked, and we can trace what went into a garment from fiber through to finished piece. Substitutions never happen without your approval.", image: "/img/Production/material-selection.jpg" },
    { title: "Cutting & Sewing", desc: "Modern cutting and high-speed sewing lines, including automated welt- and back-pocket operations. The machines give us repeatability; the operators running them are what give a garment its finish.", image: "/img/Production/Cutting Process.jpeg" },
    { title: "Quality Control", desc: "Checkpoints while the garment is still on the line, and a final audit before anything is packed. A problem caught at the needle costs a few minutes; the same problem caught at your warehouse can cost a season.", image: "/img/Production/inspection-tickets.jpg" },
    { title: "Logistics & Shipping", desc: "Packing, documentation, and routing handled to your requirements, with ex-factory dates you can plan around. Ex-factory dates are issued conservatively and confirmed in writing.", image: "/img/Production/logistics-shipping.jpg" }
  ];

  const facilities = [
    { title: "Cutting Room", desc: "Modern cutting for accuracy and better fabric yield. Less waste per garment keeps your cost down as well as ours.", image: "/img/Production/Cutting Process.jpeg" },
    { title: "Sewing Department", desc: "High-output lines with specialist machinery for the operations that decide how a finished garment looks.", image: "/img/Production/sewing-department.jpg" },
    { title: "Quality Control", desc: "Dedicated inspection zones at every stage of assembly, not a single check at the end.", image: "/img/Production/final-inspection.jpg" },
    { title: "Packaging Area", desc: "Finishing, packing, and staging handled in house, so the last step happens under the same roof as the first.", image: "/img/Production/packaging-area.jpg" }
  ];

  const standards = [
    { title: "Pre-Production", desc: "Raw materials checked and approved before a single cut is made." },
    { title: "In-Line Control", desc: "Continuous monitoring while the garment is still on the line." },
    { title: "Final Inspection", desc: "Every order examined against your specification before packing." },
    { title: "Testing Labs", desc: "Durability, shrinkage, and colour-fastness testing, so defects are identified here rather than at retail." },
    { title: "Compliance", desc: "Documented adherence to international standards, with certification available on request." }
  ];

  const fadeUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } };
  const staggerContainer = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.15 } } };

  return (
    <div className="production-modern-page">

      {/* ── Compact Banner Hero ── */}
      <section className="banner-hero" style={{ backgroundImage: `url('/img/Production/prod.jpg')` }}>
        <div className="banner-overlay"></div>
        <div className="container banner-content">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="copper-eyebrow">Own-Floor Manufacturing</span>
            <h1 className="hero-title-bold">Precision, at <span className="hero-italic">Volume.</span></h1>
          </motion.div>
        </div>
      </section>

      {/* ── High-Contrast Overview ── */}
      <section className="production-overview-section">
        <div className="container split-layout">
          <motion.div className="overview-text" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>Our Floor. Our Accountability.</h2>
            <div className="copper-divider"></div>
            <p className="lead-text">Design-led development and steady engineering on a floor we own — more than 150,000 garments a month, peaking at 5,000 a day. Owning the floor is decisive: we set the schedule, reassign a line when a date changes, and carry sole responsibility for quality.</p>
          </motion.div>
          <motion.div className="overview-stats" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="stat-block"><h3>150K+</h3><p>Garments Monthly</p></div>
            <div className="stat-block"><h3>500+</h3><p>Team Members</p></div>
          </motion.div>
        </div>
      </section>

      {/* ── Dark Mode Quality Standards ── */}
      <section className="standards-dark-section">
        <div className="container">
          <motion.div className="section-header text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="copper-eyebrow">Zero Defect Policy</span>
            <h2 className="light-text">Quality Standards</h2>
            <p className="light-subtitle">Five checkpoints between raw material and shipment. Each is documented, so the history of any order can be reconstructed from records rather than recollection.</p>
          </motion.div>
          <motion.div className="standards-grid" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            {standards.map((std, index) => (
              <motion.div className="std-card" key={index} variants={fadeUp}>
                <span className="std-num">0{index + 1}</span>
                <h4>{std.title}</h4>
                <p>{std.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Vertical Process Track ── */}
      <section className="vertical-process-section">
        <div className="container">
          <motion.div className="section-header text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>The Manufacturing Line</h2>
            <p className="subtitle">Five stages, one owner — from tech pack to ex-factory. The same team carries your order through all of them, so nothing is lost in a handover.</p>
          </motion.div>
          <div className="process-track-container">
            <div className="process-line"></div>
            {productionProcess.map((step, index) => (
              <motion.div className="process-step-row" key={index} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.4 }} variants={fadeUp}>
                <div className="process-node">{index + 1}</div>
                <div className="process-content-wrapper">
                  <div className="process-text">
                    <div className="step-rule"></div>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                  <div className="process-image-box">
                    <img src={step.image} alt={step.title} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Facilities Grid ── */}
      <section className="facilities-section">
        <div className="container">
          <motion.div className="section-header text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>Facility Infrastructure</h2>
            <p className="subtitle">The four departments an order passes through, all under one roof in Lahore.</p>
            <div className="copper-divider center-div"></div>
          </motion.div>
        </div>
        <motion.div className="facilities-grid" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }}>
          {facilities.map((fac, index) => (
            <motion.div className="fac-card" key={index} variants={fadeUp}>
              <div className="fac-media">
                <img src={fac.image} alt={fac.title} loading="lazy" />
              </div>
              <div className="fac-body">
                <span className="fac-index">0{index + 1}</span>
                <div className="fac-rule"></div>
                <h3>{fac.title}</h3>
                <p>{fac.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── NEW: Sustainability Bridge Section ── */}
      <section className="sustainability-bridge-section">
        <div className="container split-layout">
          <motion.div className="overview-text" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="copper-eyebrow">The Next Step</span>
            <h2>Earth-Conscious Operations.</h2>
            <div className="copper-divider"></div>
            <p className="lead-text">This floor runs on rooftop solar, treats its own wastewater, and specifies lower-impact materials wherever the product allows. None of this is cosmetic: it reduces operating costs, and a growing number of buyers now require it contractually.</p>
            <p>Each measure is built into daily production, monitored, and verified by an external body.</p>
            <div style={{ marginTop: "2.5rem" }}>
              <Link to="/sustainability" className="editorial-link">Explore Sustainability &rarr;</Link>
            </div>
          </motion.div>
          <motion.div className="bridge-image-wrapper" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="image-frame">
              <img src="/img/Sustainability/Sunset on Solar Panels.png" alt="Sustainability at Softwood" className="shadow-image" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── High-Contrast CTA ── */}
      <section className="clean-cta">
        <div className="container">
          <motion.div className="cta-box" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ backgroundImage: `url('/img/Production/IMG_3303.jpg')` }}>
            <div className="cta-dark-overlay"></div>
            <div className="cta-content-wrapper">
              <h2>Put Our Floor to Work.</h2>
              <a href="/contact" className="cta-link">Discuss Your Program &rarr;</a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Production;